/**
 * @file consent-platform.ts
 * @description Тексты платформы для согласия на cookie в кабинете магазина
 * (phase19.16, партия Р3г, v0.19.62; SB133 окно о риске, SB134 «текст
 * баннера ставит платформа», решение пользователя 2026-10-23 — тексты
 * платформы редакциями в базе, «Обновить» до новой редакции — владелец).
 *
 * Сервер отдаёт в черновике `consentPlatform { noticeAllowed, banner,
 * noticeRisk }` (текущие редакции). Пока полей `banner` / `noticeRisk` нет —
 * встроенная редакция 1 из shared (`BUILT_IN_*`). Здесь же — «что увидит
 * посетитель» по черновику (тот же `resolveSiteConsent`, что у сервера для
 * витрины) и что показывает канва.
 */

import {
  BUILT_IN_CONSENT_BANNER_TEXTS,
  BUILT_IN_CONSENT_TEXT_VERSION,
  BUILT_IN_NOTICE_RISK_TEXTS,
  legalDocumentHref,
  resolveSiteConsent,
  type ConsentBannerTexts,
  type ConsentNoticeRiskTexts,
  type PublicSiteConsentDto,
  type SiteConsentBannerKind,
  type SiteCounters,
} from "@chaiberi/shared";

import type { SiteConsentPlatform } from "./types";

type Platform = SiteConsentPlatform | undefined;

/** Разрешён ли платформой режим «Уведомление»; поля нет — разрешён (умолчание платформы, db-design-r3g §3). */
export function noticeAllowedOf(platform: Platform): boolean {
  return platform?.noticeAllowed !== false;
}

/** Окно о риске: показанные тексты и номер их редакции — он уходит в `noticeAck`. */
export interface NoticeRiskEdition {
  version: number;
  texts: ConsentNoticeRiskTexts;
}

export function noticeRiskOf(platform: Platform): NoticeRiskEdition {
  const edition = platform?.noticeRisk;
  return edition ? { version: edition.currentVersion, texts: edition.texts } : { version: BUILT_IN_CONSENT_TEXT_VERSION, texts: BUILT_IN_NOTICE_RISK_TEXTS };
}

/** Текущая редакция текстов баннера на платформе. */
export function currentBannerVersion(platform: Platform): number {
  return platform?.banner?.currentVersion ?? BUILT_IN_CONSENT_TEXT_VERSION;
}

/** Редакция черновика (`counters.platformTextVersion`): тексты и дата; null — сервер её не отдал. */
function draftEdition(counters: Pick<SiteCounters, "platformTextVersion">, platform: Platform): { texts: ConsentBannerTexts; publishedAt: string | null } | null {
  const banner = platform?.banner;
  const version = counters.platformTextVersion;
  if (banner?.currentVersion === version) return { texts: banner.texts, publishedAt: banner.publishedAt };
  if (banner?.draft?.version === version) return { texts: banner.draft.texts, publishedAt: banner.draft.publishedAt };
  return null;
}

/**
 * Тексты редакции черновика: текущая — `banner.texts`, не текущая —
 * `banner.draft.texts`; сервер без редакций (или редакция 1) — встроенные
 * из shared.
 */
export function bannerTextsFor(counters: Pick<SiteCounters, "platformTextVersion">, platform: Platform): ConsentBannerTexts {
  return draftEdition(counters, platform)?.texts ?? BUILT_IN_CONSENT_BANNER_TEXTS;
}

/** Номер новой редакции, до которой можно «Обновить»; null — черновик на текущей. */
export function bannerTextsUpdate(counters: Pick<SiteCounters, "platformTextVersion">, platform: Platform): number | null {
  const current = currentBannerVersion(platform);
  return counters.platformTextVersion < current ? current : null;
}

/** «Редакция 2 от 23.10.2026» — по дате редакции черновика; у встроенной даты нет. */
export function bannerEditionLabel(counters: Pick<SiteCounters, "platformTextVersion">, platform: Platform): string {
  const version = counters.platformTextVersion;
  const at = draftEdition(counters, platform)?.publishedAt ?? null;
  return at ? `Редакция ${version} от ${new Date(at).toLocaleDateString("ru-RU")}` : `Редакция ${version}`;
}

// ── Что увидит посетитель ──

/** Адрес политики ПДн для ссылки «Подробнее» на канве (на сайте её ставит сервер, если политика видна). */
export const CONSENT_POLICY_HREF = legalDocumentHref("privacy-policy");

/** Блок согласия по черновику — тот же расчёт, что у сервера для витрины. */
export function draftConsent(counters: SiteCounters, noticeAllowed: boolean, texts: ConsentBannerTexts): PublicSiteConsentDto {
  return resolveSiteConsent(counters, { noticeAllowed, consentVersion: 0, policyHref: CONSENT_POLICY_HREF, texts });
}

/** Текст баннера, как его увидит посетитель; null — баннера на сайте не будет. */
export function bannerPreviewText(counters: SiteCounters, noticeAllowed: boolean, texts: ConsentBannerTexts): string | null {
  return draftConsent(counters, noticeAllowed, texts).banner?.text ?? null;
}

// ── Канва ──

/** Что показывает канва: баннер или баннер с окном «Настроить». */
export type ConsentCanvasView = "banner" | "settings";

/** Есть ли у баннера кнопка «Настроить» (SB134: «после согласия» и «Уведомление»; у уведомления без счётчиков — нет). */
export function hasConsentSettings(kind: SiteConsentBannerKind | null): boolean {
  return kind === "consent" || kind === "notice";
}

/**
 * Вид канвы по вкладке адреса (`panel=counters.settings`) и виду баннера:
 * окно «Настроить» — только когда оно у баннера есть, иначе баннер (вкладка,
 * оставшаяся от прошлого вида, не держит окно на канве).
 */
export function consentCanvasView(sub: string | null, kind: SiteConsentBannerKind | null): ConsentCanvasView {
  return sub === "settings" && hasConsentSettings(kind) ? "settings" : "banner";
}
